/**
 * Token metadata fetching from multiple sources:
 * 1. Shared Jupiter token list (from portfolio hook - ~9000 tokens with logos)
 * 2. Helius DAS API (covers unverified/newer tokens)
 * 3. Metaplex on-chain metadata (last resort)
 */

import { Connection, PublicKey } from "@solana/web3.js";
import {
  getJupiterTokenMetadata,
  fetchJupiterTokenList,
  getJupiterTokenListCache,
  isJupiterCacheReady,
} from "@/hooks/use-token-balances";

/** Metaplex Token Metadata Program ID */
const METAPLEX_METADATA_PROGRAM_ID = new PublicKey(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);

/** Token metadata from any source */
export interface TokenMetadata {
  address: string;
  symbol: string;
  name: string;
  decimals?: number;
  logoURI?: string;
  tags?: string[];
  source?: "jupiter" | "helius-das" | "metaplex" | "unknown";
}

/** In-memory cache for token metadata (session-scoped) */
const metadataCache = new Map<string, TokenMetadata>();

/**
 * Get the base URL for API calls
 */
function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "";
}

/**
 * Look up metadata from the shared Jupiter token list cache.
 * This is the same cache used by the portfolio sidebar (~9000 tokens).
 */
async function lookupFromSharedJupiterCache(
  mintAddress: string
): Promise<TokenMetadata | null> {
  const jupiterMeta = await getJupiterTokenMetadata(mintAddress);
  if (jupiterMeta) {
    return {
      address: mintAddress,
      symbol: jupiterMeta.symbol,
      name: jupiterMeta.name,
      logoURI: jupiterMeta.logoURI,
      source: "jupiter",
    };
  }
  return null;
}

/**
 * Fetch metadata for a single token via Helius DAS API
 * Uses our proxy at /api/helius-token/{mint} to keep API key server-side
 */
async function fetchHeliusTokenMetadata(mintAddress: string): Promise<TokenMetadata | null> {
  // Check cache first
  const cached = metadataCache.get(mintAddress);
  if (cached) {
    return cached;
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    const response = await fetch(`${getBaseUrl()}/api/helius-token/${mintAddress}`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (!data.success) {
      return null;
    }

    const metadata: TokenMetadata = {
      address: mintAddress,
      name: data.name || "",
      symbol: data.symbol || "",
      logoURI: data.logoURI || "",
      source: "helius-das",
    };

    if (metadata.name || metadata.symbol) {
      metadataCache.set(mintAddress, metadata);
      return metadata;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch metadata for multiple tokens via Helius DAS API batch endpoint
 * Uses single getAssetBatch call instead of N individual calls
 */
async function fetchHeliusTokenMetadataBatch(
  mintAddresses: string[]
): Promise<Map<string, TokenMetadata>> {
  const result = new Map<string, TokenMetadata>();

  // Filter out already cached addresses
  const uncached = mintAddresses.filter((addr) => {
    const cached = metadataCache.get(addr);
    if (cached) {
      result.set(addr, cached);
      return false;
    }
    return true;
  });

  if (uncached.length === 0) {
    return result;
  }

  // Process in batches of 100 (Helius limit)
  const BATCH_SIZE = 100;

  for (let i = 0; i < uncached.length; i += BATCH_SIZE) {
    const batch = uncached.slice(i, i + BATCH_SIZE);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${getBaseUrl()}/api/helius-token-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mints: batch }),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!response.ok) {
        console.warn(`[metadata] Batch fetch failed: ${response.status}`);
        continue;
      }

      const data = await response.json();

      if (data.success && data.tokens) {
        for (const [mint, tokenData] of Object.entries(data.tokens)) {
          const token = tokenData as { name: string; symbol: string; logoURI: string };
          const metadata: TokenMetadata = {
            address: mint,
            name: token.name || "",
            symbol: token.symbol || "",
            logoURI: token.logoURI || "",
            source: "helius-das",
          };

          if (metadata.name || metadata.symbol) {
            result.set(mint, metadata);
            metadataCache.set(mint, metadata);
          }
        }
      }
    } catch (error) {
      console.warn(`[metadata] Batch fetch error:`, error);
      // Continue with next batch
    }

    // Small delay between batches to be nice to the API
    if (i + BATCH_SIZE < uncached.length) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  return result;
}

/**
 * Derive the Metaplex Metadata PDA for a mint
 */
function getMetadataPDA(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("metadata"), METAPLEX_METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    METAPLEX_METADATA_PROGRAM_ID
  );
  return pda;
}

/**
 * Decode Metaplex metadata from account data
 */
function decodeMetaplexMetadata(
  data: Buffer
): { name: string; symbol: string; uri: string } | null {
  try {
    let offset = 1 + 32 + 32; // Skip key, update authority, mint

    const nameLen = data.readUInt32LE(offset);
    offset += 4;
    const name = data
      .slice(offset, offset + nameLen)
      .toString("utf8")
      .replace(/\0/g, "")
      .trim();
    offset += 32;

    const symbolLen = data.readUInt32LE(offset);
    offset += 4;
    const symbol = data
      .slice(offset, offset + symbolLen)
      .toString("utf8")
      .replace(/\0/g, "")
      .trim();
    offset += 10;

    const uriLen = data.readUInt32LE(offset);
    offset += 4;
    const uri = data
      .slice(offset, offset + uriLen)
      .toString("utf8")
      .replace(/\0/g, "")
      .trim();

    if (name || symbol) {
      return { name, symbol, uri };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch metadata from Metaplex on-chain metadata account
 */
export async function fetchMetaplexMetadata(
  connection: Connection,
  mintAddress: string
): Promise<TokenMetadata | null> {
  const cached = metadataCache.get(mintAddress);
  if (cached) {
    return cached;
  }

  try {
    const mint = new PublicKey(mintAddress);
    const metadataPDA = getMetadataPDA(mint);

    const accountInfo = await connection.getAccountInfo(metadataPDA);

    if (!accountInfo || !accountInfo.data) {
      return null;
    }

    const decoded = decodeMetaplexMetadata(Buffer.from(accountInfo.data));

    if (decoded) {
      const metadata: TokenMetadata = {
        address: mintAddress,
        symbol: decoded.symbol,
        name: decoded.name,
        source: "metaplex",
      };

      // Try to fetch logo from URI
      if (decoded.uri && decoded.uri.startsWith("http")) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 5000);
          const uriResponse = await fetch(decoded.uri, { signal: controller.signal });
          clearTimeout(timeout);
          if (uriResponse.ok) {
            const json = await uriResponse.json();
            if (json.image) {
              metadata.logoURI = json.image;
            }
          }
        } catch {
          // Ignore URI fetch errors
        }
      }

      metadataCache.set(mintAddress, metadata);
      return metadata;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Get metadata for a token, trying all sources
 * Priority: Shared Jupiter cache -> Helius DAS -> Metaplex
 */
export async function getTokenMetadataWithFallback(
  connection: Connection,
  mintAddress: string
): Promise<TokenMetadata | null> {
  // 1. Check local cache first
  const cached = metadataCache.get(mintAddress);
  if (cached) {
    return cached;
  }

  // 2. Try shared Jupiter token list (from portfolio - ~9000 tokens)
  const jupiterMetadata = await lookupFromSharedJupiterCache(mintAddress);
  if (jupiterMetadata) {
    metadataCache.set(mintAddress, jupiterMetadata);
    return jupiterMetadata;
  }

  // 3. Try Helius DAS API (covers unverified/newer tokens)
  const heliusMetadata = await fetchHeliusTokenMetadata(mintAddress);
  if (heliusMetadata && (heliusMetadata.name || heliusMetadata.symbol)) {
    return heliusMetadata;
  }

  // 4. Try Metaplex on-chain metadata (last resort)
  const metaplexMetadata = await fetchMetaplexMetadata(connection, mintAddress);
  if (metaplexMetadata) {
    return metaplexMetadata;
  }

  return null;
}

/**
 * Get metadata for multiple tokens, using all available sources
 */
export async function enrichTokensWithMetadata(
  connection: Connection,
  mintAddresses: string[]
): Promise<Map<string, TokenMetadata>> {
  const result = new Map<string, TokenMetadata>();

  if (mintAddresses.length === 0) {
    return result;
  }

  // Ensure shared Jupiter cache is loaded (MUST await - cache may be empty otherwise)
  await fetchJupiterTokenList();

  const jupiterCache = getJupiterTokenListCache();

  // 1. Check shared Jupiter token list first (fast, shared with portfolio)
  const needsFallback: string[] = [];

  for (const addr of mintAddresses) {
    // Check local cache first
    const localCached = metadataCache.get(addr);
    if (localCached) {
      result.set(addr, localCached);
      continue;
    }

    // Check shared Jupiter cache
    const jupiterMeta = jupiterCache?.get(addr);
    if (jupiterMeta && jupiterMeta.logoURI) {
      const metadata: TokenMetadata = {
        address: addr,
        symbol: jupiterMeta.symbol,
        name: jupiterMeta.name,
        logoURI: jupiterMeta.logoURI,
        source: "jupiter",
      };
      result.set(addr, metadata);
      metadataCache.set(addr, metadata);
    } else {
      needsFallback.push(addr);
    }
  }

  // 2. Try Helius DAS API for remaining tokens
  if (needsFallback.length > 0) {
    const heliusResults = await fetchHeliusTokenMetadataBatch(needsFallback);

    for (const [addr, meta] of heliusResults) {
      if (meta.name || meta.symbol) {
        result.set(addr, meta);
      }
    }
  }

  // 3. Last resort: Metaplex for remaining tokens
  const needsMetaplex = mintAddresses.filter((addr) => !result.has(addr));
  if (needsMetaplex.length > 0) {
    const BATCH_SIZE = 5;
    for (let i = 0; i < needsMetaplex.length; i += BATCH_SIZE) {
      const batch = needsMetaplex.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (address) => {
        const metadata = await fetchMetaplexMetadata(connection, address);
        if (metadata) {
          result.set(address, metadata);
        }
      });
      await Promise.all(promises);
    }
  }

  return result;
}

/**
 * Get display name for a token
 */
export function getTokenDisplayName(
  mintAddress: string,
  metadata?: TokenMetadata | null
): string {
  if (metadata?.symbol && metadata?.name) {
    return `${metadata.symbol} - ${metadata.name}`;
  }
  if (metadata?.symbol) {
    return metadata.symbol;
  }
  if (metadata?.name) {
    return metadata.name;
  }
  return `${mintAddress.slice(0, 4)}...${mintAddress.slice(-4)}`;
}

/**
 * Clear all metadata caches (useful for refresh)
 */
export function clearMetadataCache(): void {
  metadataCache.clear();
}
